// Category-specific form configurations
export const categoryFormConfigs = {
  "Printing & Packaging": {
    steps: [
      {
        id: 1,
        title: "Tell Us More",
        description: "Describe your printing or packaging requirement",
        fields: ["title", "samples"]
      },
      {
        id: 2,
        title: "Quantity & Specifications",
        description: "Specify quantity and technical details",
        fields: ["quantity", "specifications", "frequency"]
      },
      {
        id: 3,
        title: "Timeline",
        description: "When do you need this completed?",
        fields: ["urgency"]
      },
      {
        id: 4,
        title: "Location",
        description: "Where should vendors be located?",
        fields: ["location"]
      },
      {
        id: 5,
        title: "Budget",
        description: "What's your budget range?",
        fields: ["budget"]
      },
      {
        id: 6,
        title: "Contact Information",
        description: "How should vendors reach you?",
        fields: ["fullName", "whatsapp", "email", "allowWhatsapp"]
      },
      {
        id: 7,
        title: "Preview & Confirm",
        description: "Review your request before posting",
        fields: ["preview"]
      }
    ],
    fieldConfigs: {
      specifications: {
        type: "textarea",
        placeholder: "e.g., A4 size, 300gsm paper, full color printing, matte finish",
        label: "Technical Specifications",
        required: false
      },
      samples: {
        type: "conditional-file-upload",
        label: "Do you have samples to upload?",
        description: "Reference images or samples help vendors understand your requirements better",
        accept: "image/*,.pdf,.doc,.docx",
        maxFiles: 5,
        options: [
          { value: "no", label: "No, I don't have samples" },
          { value: "yes", label: "Yes, I have samples" }
        ]
      },
      frequency: {
        type: "radio",
        options: [
          { value: "one-time", label: "One-time" },
          { value: "recurring", label: "Recurring" }
        ],
        label: "Order Frequency"
      }
    }
  },
  
  "Housing & Rentals": {
    steps: [
      {
        id: 1,
        title: "Tell Us More",
        description: "Describe the property you're looking for",
        fields: ["title"]
      },
      {
        id: 2,
        title: "Property Details",
        description: "Specify property requirements",
        fields: ["propertyType", "bedrooms", "bathrooms", "furnished"]
      },
      {
        id: 3,
        title: "Lease Terms",
        description: "What are your lease requirements?",
        fields: ["leaseDuration", "moveInDate", "maxRent"]
      },
      {
        id: 4,
        title: "Location Preferences",
        description: "Where would you like to live?",
        fields: ["location"]
      },
      {
        id: 5,
        title: "Budget",
        description: "What's your monthly rent budget?",
        fields: ["budget"]
      },
      {
        id: 6,
        title: "Contact Information",
        description: "How should landlords reach you?",
        fields: ["fullName", "whatsapp", "email", "allowWhatsapp"]
      },
      {
        id: 7,
        title: "Preview & Confirm",
        description: "Review your rental request",
        fields: ["preview"]
      }
    ],
    fieldConfigs: {
      propertyType: {
        type: "select",
        options: [
          { value: "apartment", label: "Apartment" },
          { value: "house", label: "House" },
          { value: "studio", label: "Studio" },
          { value: "shared", label: "Shared Room" },
          { value: "office", label: "Office Space" }
        ],
        label: "Property Type"
      },
      bedrooms: {
        type: "select",
        options: [
          { value: "1", label: "1 Bedroom" },
          { value: "2", label: "2 Bedrooms" },
          { value: "3", label: "3 Bedrooms" },
          { value: "4+", label: "4+ Bedrooms" }
        ],
        label: "Number of Bedrooms"
      },
      bathrooms: {
        type: "select",
        options: [
          { value: "1", label: "1 Bathroom" },
          { value: "2", label: "2 Bathrooms" },
          { value: "3+", label: "3+ Bathrooms" }
        ],
        label: "Number of Bathrooms"
      },
      furnished: {
        type: "radio",
        options: [
          { value: "furnished", label: "Furnished" },
          { value: "unfurnished", label: "Unfurnished" },
          { value: "semi-furnished", label: "Semi-furnished" }
        ],
        label: "Furnishing"
      },
      leaseDuration: {
        type: "select",
        options: [
          { value: "short-term", label: "Short-term (1-6 months)" },
          { value: "long-term", label: "Long-term (6+ months)" },
          { value: "flexible", label: "Flexible" }
        ],
        label: "Lease Duration"
      },
      moveInDate: {
        type: "date",
        label: "Preferred Move-in Date"
      },
      maxRent: {
        type: "input",
        placeholder: "e.g., ₦500,000 per month",
        label: "Maximum Monthly Rent"
      }
    }
  },
  
  "Cleaning Services": {
    steps: [
      {
        id: 1,
        title: "Tell Us More",
        description: "Describe your cleaning needs",
        fields: ["title"]
      },
      {
        id: 2,
        title: "Service Details",
        description: "Specify service requirements",
        fields: ["serviceType", "propertySize", "frequency"]
      },
      {
        id: 3,
        title: "Timeline",
        description: "When do you need the service?",
        fields: ["urgency"]
      },
      {
        id: 4,
        title: "Location",
        description: "Where is the property located?",
        fields: ["location"]
      },
      {
        id: 5,
        title: "Budget",
        description: "What's your budget for this service?",
        fields: ["budget"]
      },
      {
        id: 6,
        title: "Contact Information",
        description: "How should cleaners reach you?",
        fields: ["fullName", "whatsapp", "email", "allowWhatsapp"]
      },
      {
        id: 7,
        title: "Preview & Confirm",
        description: "Review your cleaning request",
        fields: ["preview"]
      }
    ],
    fieldConfigs: {
      serviceType: {
        type: "select",
        options: [
          { value: "regular", label: "Regular Cleaning" },
          { value: "deep", label: "Deep Cleaning" },
          { value: "post-construction", label: "Post-Construction" },
          { value: "move-in", label: "Move-in/Move-out" },
          { value: "office", label: "Office Cleaning" }
        ],
        label: "Service Type"
      },
      propertySize: {
        type: "select",
        options: [
          { value: "small", label: "Small (1-2 rooms)" },
          { value: "medium", label: "Medium (3-4 rooms)" },
          { value: "large", label: "Large (5+ rooms)" },
          { value: "commercial", label: "Commercial Space" }
        ],
        label: "Property Size"
      }
    }
  },
  
  "Food Supply": {
    steps: [
      {
        id: 1,
        title: "Tell Us More",
        description: "Describe your food supply requirements",
        fields: ["title"]
      },
      {
        id: 2,
        title: "Quantity & Quality",
        description: "Specify quantity and quality requirements",
        fields: ["quantity", "quality", "frequency"]
      },
      {
        id: 3,
        title: "Timeline",
        description: "When do you need delivery?",
        fields: ["urgency"]
      },
      {
        id: 4,
        title: "Location",
        description: "Where should suppliers deliver?",
        fields: ["location"]
      },
      {
        id: 5,
        title: "Budget",
        description: "What's your budget for food items?",
        fields: ["budget"]
      },
      {
        id: 6,
        title: "Contact Information",
        description: "How should suppliers reach you?",
        fields: ["fullName", "whatsapp", "email", "allowWhatsapp"]
      },
      {
        id: 7,
        title: "Preview & Confirm",
        description: "Review your food supply request",
        fields: ["preview"]
      }
    ],
    fieldConfigs: {
      quality: {
        type: "select",
        options: [
          { value: "premium", label: "Premium Quality" },
          { value: "standard", label: "Standard Quality" },
          { value: "bulk", label: "Bulk/Wholesale" }
        ],
        label: "Quality Preference"
      }
    }
  },
  
  "Trade & Repairs": {
    steps: [
      {
        id: 1,
        title: "Tell Us More",
        description: "Describe the repair or trade work needed",
        fields: ["title", "repairPhotos"]
      },
      {
        id: 2,
        title: "Work Details",
        description: "Specify the scope of work",
        fields: ["workType", "complexity", "frequency"]
      },
      {
        id: 3,
        title: "Timeline",
        description: "When do you need this completed?",
        fields: ["urgency"]
      },
      {
        id: 4,
        title: "Location",
        description: "Where is the work site?",
        fields: ["location"]
      },
      {
        id: 5,
        title: "Budget",
        description: "What's your budget for this work?",
        fields: ["budget"]
      },
      {
        id: 6,
        title: "Contact Information",
        description: "How should contractors reach you?",
        fields: ["fullName", "whatsapp", "email", "allowWhatsapp"]
      },
      {
        id: 7,
        title: "Preview & Confirm",
        description: "Review your repair request",
        fields: ["preview"]
      }
    ],
    fieldConfigs: {
      workType: {
        type: "select",
        options: [
          { value: "electrical", label: "Electrical Work" },
          { value: "plumbing", label: "Plumbing" },
          { value: "carpentry", label: "Carpentry" },
          { value: "painting", label: "Painting" },
          { value: "general", label: "General Repairs" }
        ],
        label: "Type of Work"
      },
      complexity: {
        type: "select",
        options: [
          { value: "simple", label: "Simple (1-2 hours)" },
          { value: "moderate", label: "Moderate (Half day)" },
          { value: "complex", label: "Complex (Full day+)" }
        ],
        label: "Work Complexity"
      },
      repairPhotos: {
        type: "conditional-file-upload",
        label: "Do you have photos of the issue?",
        description: "Photos help contractors understand the problem better and provide more accurate quotes",
        accept: "image/*",
        maxFiles: 5,
        options: [
          { value: "yes", label: "Yes, I have photos" },
          { value: "no", label: "No, I don't have photos" }
        ]
      }
    }
  }
};

// Default form configuration for categories not specifically defined
export const defaultFormConfig = {
  steps: [
    {
      id: 1,
      title: "Tell Us More",
      description: "Describe your requirement in detail",
      fields: ["title"]
    },
    {
      id: 2,
      title: "Quantity & Frequency",
      description: "Specify the quantity or volume you need",
      fields: ["quantity", "frequency"]
    },
    {
      id: 3,
      title: "Timeline",
      description: "When do you need this?",
      fields: ["urgency"]
    },
    {
      id: 4,
      title: "Location",
      description: "Where should vendors be located?",
      fields: ["location"]
    },
    {
      id: 5,
      title: "Budget",
      description: "What's your budget?",
      fields: ["budget"]
    },
    {
      id: 6,
      title: "Contact Information",
      description: "How should vendors reach you?",
      fields: ["fullName", "whatsapp", "email", "allowWhatsapp"]
    },
    {
      id: 7,
      title: "Preview & Confirm",
      description: "Review your request before posting",
      fields: ["preview"]
    }
  ],
  fieldConfigs: {}
};

// Helper function to get form configuration for a category
export const getFormConfig = (categoryName) => {
  return categoryFormConfigs[categoryName] || defaultFormConfig;
};

// Helper function to get field configuration for a specific field
export const getFieldConfig = (categoryName, fieldName) => {
  const config = getFormConfig(categoryName);
  return config.fieldConfigs[fieldName] || null;
}; 